import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    IUserCountryDetails,
    TBuyboxEnabledData,
    TGoalsConfig,
} from "@ts/common-types";

export interface IAdminSettingsState {
    dayparting: boolean;
    buybox: boolean;
    revstack: boolean;
    keywordRecommendation: boolean;
    navigator: boolean;
    categoryAnalysis: boolean;
    productAnalysis: boolean;
}

export interface IAdminToggleBtn {
    enableDayparting?: {
        value: boolean;
    };
    enableBuybox?: {
        value: boolean;
    };
    enableRevstack?: {
        value: boolean;
    };
    enableKeywordRecommendation: {
        value: boolean;
    };
    enableNavigator?: {
        value: boolean;
    };
    enableCategoryAnalysis?: {
        value: boolean;
    };
    enableProductAnalysis?: {
        value: boolean;
    };
}

const adminSettingsSlice = createSlice({
    name: "adminSettings",
    initialState: {
        value: {
            dayparting: false,
            buybox: false,
            revstack: false,
            keywordRecommendation: false,
            navigator: false,
            categoryAnalysis: false,
            productAnalysis: false,
        } as IAdminSettingsState,
    },
    reducers: {

        updateAllAdminSettings: (state, action: PayloadAction<Partial<IAdminSettingsState>>) => {
            state.value = { ...state.value, ...action.payload };
        },
        updateEnableDayparting: (state, action: PayloadAction<boolean>) => {
            state.value.dayparting = action.payload;
        },
        updateEnableBuybox: (state, action: PayloadAction<boolean>) => {
            state.value.buybox = action.payload;
        },
        updateEnableRevstack: (state, action: PayloadAction<boolean>) => {
            state.value.revstack = action.payload;
        },
        updateEnableKeywordRecommendation: (state, action: PayloadAction<boolean>) => {
            state.value.keywordRecommendation = action.payload;
        },
        updateEnableNavigator: (state, action: PayloadAction<boolean>) => {
            state.value.navigator = action.payload;
        },
        updateEnableCategoryAnalysis: (state, action: PayloadAction<boolean>) => {
            state.value.categoryAnalysis = action.payload;
        },
        updateEnableProductAnalysis: (state, action: PayloadAction<boolean>) => {
            state.value.productAnalysis = action.payload;
        },
    },
});

const clientTimezoneSlice = createSlice({
    name: "clientTimezone",
    initialState: { value: "" },
    reducers: {
        updateClientTimezone: (state, action: PayloadAction<string>) => {
            state.value = action.payload;
        },
    },
});

const buyboxEnabledDataSlice = createSlice({
    name: "buyboxEnabledData",
    initialState: { value: [] },
    reducers: {
        updateBuyboxEnabledData: (
            state,
            action: PayloadAction<TBuyboxEnabledData>
        ) => {
            state.value = action.payload;
        },
    },
});

const isBuyboxUpdatedSlice = createSlice({
    name: "isBuyboxUpdated",
    initialState: { value: false },
    reducers: {
        updateIsBuyboxUpdated: (state, action: PayloadAction<boolean>) => {
            state.value = action.payload;
        },
    },
});

const focusedAsinsSlice = createSlice({
    name: "focusedAsins",
    initialState: { value: [] },
    reducers: {
        setFocusedAsins: (state, action: PayloadAction<string[]>) => {
            state.value = action.payload;
        },
    },
});

const doPreferredSellersExistSlice = createSlice({
    name: "doPreferredSellersExist",
    initialState: { value: false },
    reducers: {
        updateDoPreferredSellersExist: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.value = action.payload;
        },
    },
});

const userCountryDetailsSlice = createSlice({
    name: "userCountryDetails",
    initialState: {
        value: {
            selected_country_code: "US",
            // country_name:"",
            selected_country_locale: [],
        },
    },
    reducers: {
        updateUserCountryDetails: (
            state,
            action: PayloadAction<IUserCountryDetails>
        ) => {
            state.value = action.payload;
        },
    },
});

const goalsConfigslice = createSlice({
    name: "goalsConfig",
    initialState: { value: [] },
    reducers: {
        setGoalsConfig: (state, action: PayloadAction<TGoalsConfig[]>) => {
            state.value = action.payload;
        },
    },
});

export const { 
    updateAllAdminSettings,
    updateEnableDayparting,
    updateEnableBuybox,
    updateEnableRevstack,
    updateEnableKeywordRecommendation,
    updateEnableNavigator,
    updateEnableCategoryAnalysis,
    updateEnableProductAnalysis,
} = adminSettingsSlice.actions;
export const { updateIsBuyboxUpdated } = isBuyboxUpdatedSlice.actions;
export const { updateClientTimezone } = clientTimezoneSlice.actions;
export const { updateBuyboxEnabledData } = buyboxEnabledDataSlice.actions;
export const { updateDoPreferredSellersExist } =
    doPreferredSellersExistSlice.actions;
export const { updateUserCountryDetails } = userCountryDetailsSlice.actions;
export const { setFocusedAsins } = focusedAsinsSlice.actions;
export const { setGoalsConfig } = goalsConfigslice.actions;

export const adminSettingsReducer = adminSettingsSlice.reducer;
export const clientTimezoneReducer = clientTimezoneSlice.reducer;
export const buyboxEnabledDataReducer = buyboxEnabledDataSlice.reducer;
export const isBuyboxUpdatedReducer = isBuyboxUpdatedSlice.reducer;
export const doPreferredSellersExistReducer =
    doPreferredSellersExistSlice.reducer;
export const userCountryDetailsReducer = userCountryDetailsSlice.reducer;
export const focusedAsinsReducer = focusedAsinsSlice.reducer;
export const goalsConfigReducer = goalsConfigslice.reducer;
